import {
  useKineticMap,
  useStableResultData,
  useGetNodes,
  MapContext,
  MapSearchResultItem,
  useMapBoxSearchContext,
  DEFAULT_ZOOM,
  useMapSearchBoxPin,
  MapSearchResultProps,
  useAppSearchHistory,
  publicUrl,
  AppSearchItemType,
  AppSearchIconKey,
} from '@kinetic-ui/shared';
import {
  RampMeterMapSearchResultQuery,
  useRampMeterMapSearchResultQuery,
} from '@kinetic-ui/data-layer';
import { useCallback, useContext, useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import { ENTITY_DRAWER_VIEW_ALL_ROUTE, ENTITY_EDIT_ROUTE } from '../EntityList/consts';
import { RampMeterStatusLayers, StatusLayerIds } from '../StatusLayers/const';
import { MapSearchResultMarker } from '../StatusLayers/utils/getMarkers';

const MapSearchResult: React.FC<MapSearchResultProps> = ({ isAppSearch, onResultClick }) => {
  const map = useKineticMap();
  const { searchText } = useMapBoxSearchContext();
  const { selectedLayers, setSelectedLayers, hasEntityDrawer } = useContext(MapContext);
  const { addMapPin, removeMapPin } = useMapSearchBoxPin();
  const { push } = useHistory();
  const { addSearchHistory } = useAppSearchHistory();

  const result = useRampMeterMapSearchResultQuery({
    fetchPolicy: 'cache-and-network',
    variables: { searchText, limit: 5 },
  });
  const resultData = useStableResultData(result);
  const rampMeters = useGetNodes(resultData?.rampMeters);

  const getName = useCallback(
    (rampMeter: RampMeterMapSearchResultQuery['rampMeters']['edges'][0]['node']) => {
      const rampMeterName = rampMeter.details?.name ?? '';
      const deviceName = rampMeter.devices?.map((device) => device.name)?.join(' / ');

      if (deviceName) {
        return `${rampMeterName} / ${deviceName}`;
      } else {
        return rampMeterName;
      }
    },
    []
  );

  const onClick = useCallback(
    (id: string, coordinates: [number, number], openInNewWindow?: boolean) => {
      if (isAppSearch) {
        const path = `/${ENTITY_EDIT_ROUTE}/${id}/`;
        if (openInNewWindow) {
          window.open(publicUrl(path), '_blank');
        } else {
          push(path);
        }
        const meter = rampMeters.find((e) => e.id === id);
        let meterName = meter ? getName(meter) : null;
        if (meterName) {
          addSearchHistory({
            name: meterName,
            link: path,
            type: AppSearchItemType.View,
            icon: AppSearchIconKey.RampMeter,
          });
        }
        if (onResultClick) {
          onResultClick();
        }
        return;
      }

      if (!hasEntityDrawer) {
        addMapPin({ type: 'Point', coordinates: coordinates ?? [0, 0] });
        return;
      }
      if (
        !selectedLayers.some((layerId) => RampMeterStatusLayers.includes(layerId as StatusLayerIds))
      ) {
        setSelectedLayers([
          ...selectedLayers,
          StatusLayerIds.METER_PRIMARY_LANE_STATUS_LAYER_ID,
          StatusLayerIds.METER_DETAIL_STATUS_LAYER_ID,
        ]);
      }
      push(`/${ENTITY_DRAWER_VIEW_ALL_ROUTE}/${id}`);
      map.setViewState({
        latitude: coordinates[1],
        longitude: coordinates[0],
        zoom: DEFAULT_ZOOM,
      });
    },
    [
      addMapPin,
      addSearchHistory,
      getName,
      hasEntityDrawer,
      isAppSearch,
      map,
      onResultClick,
      push,
      rampMeters,
      selectedLayers,
      setSelectedLayers,
    ]
  );

  useEffect(() => {
    if (!hasEntityDrawer && searchText?.length > 0) {
      removeMapPin();
    }
    // only do this when searchText changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchText]);

  return (
    <>
      {rampMeters.map((rampMeter) => (
        <MapSearchResultItem
          key={`searchItem_${rampMeter.id}`}
          name={getName(rampMeter)}
          onClick={(openInNewWindow) =>
            onClick(rampMeter.id, rampMeter.location?.coordinates, openInNewWindow)
          }
          icon={<MapSearchResultMarker />}
          newWindow={isAppSearch}
        />
      ))}
    </>
  );
};

export default MapSearchResult;
